// The shared building blocks pages are made from, so every page looks and
// behaves the same. Styles in styles/app.css.
import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, useId } from 'react';
import { Link } from 'react-router-dom';
import type { InvoiceStatus } from '@/core/invoice';
import { statusLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { Icon, type IconName } from '@/ui/Icon';

type Variant = 'action' | 'secondary' | 'ghost' | 'danger' | 'danger-solid' | 'soft' | 'ai' | 'signal' | 'whatsapp' | 'pro' | 'plain';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  busy?: boolean;
  block?: boolean;
}

export function buttonClass(variant: Variant = 'secondary', size: 'sm' | 'md' | 'lg' = 'md', block = false, extra = '') {
  return ['btn', variant !== 'plain' && `btn--${variant}`, size !== 'md' && `btn--${size}`, block && 'btn--block', extra].filter(Boolean).join(' ');
}

/** One `action` button per page: the main thing to do there. */
export function Button({ variant = 'secondary', size = 'md', icon, busy = false, block = false, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, block, className)} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? <span className="btn__spinner" aria-hidden /> : icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} />}
      {children}
    </button>
  );
}

export function ButtonLink({ to, variant = 'secondary', size = 'md', icon, block = false, children, className }: { to: string; variant?: Variant; size?: 'sm' | 'md' | 'lg'; icon?: IconName; block?: boolean; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={buttonClass(variant, size, block, className)}>
      {icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} />}
      {children}
    </Link>
  );
}

export function IconButton({ icon, label, size, outlined, ...rest }: { icon: IconName; label: string; size?: 'sm'; outlined?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={['iconbtn', size === 'sm' && 'iconbtn--sm', outlined && 'iconbtn--outlined'].filter(Boolean).join(' ')} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size === 'sm' ? 18 : 20} />
    </button>
  );
}

/** The P-lark mark (docs/design/logo) on its forest tile. */
export function LogoMark({ size = 34 }: { size?: number }) {
  const small = size * 0.72 < 24;
  return (
    <span style={{ width: size, height: size, borderRadius: Math.round(size * 0.32), background: 'var(--brand-deep)', color: 'var(--signal)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <svg width={Math.round(size * 0.72)} height={Math.round(size * 0.72)} viewBox="0 0 64 64" aria-hidden>
        <path d="M19 57 V13 H34 C44.5 13 50 19.5 50 27.5 C50 35.5 44.5 42 34 42 H19" fill="none" stroke="currentColor" strokeWidth={small ? 10 : 8.5} strokeLinejoin="round" strokeLinecap="round" />
        <path d={small ? 'M53 22 L63 27.5 L53 33 Z' : 'M53.5 23 L62.5 27.5 L53.5 32 Z'} fill="currentColor" />
        {!small && <circle cx="40.5" cy="24" r="2.6" fill="currentColor" />}
      </svg>
    </span>
  );
}

/** A status always says its word; colour only backs it up. */
export function StatusChip({ status, children }: { status: InvoiceStatus; children?: ReactNode }) {
  return (
    <span className={`chip chip--${status}`}>
      <i aria-hidden />
      {children ?? statusLabel(status)}
    </span>
  );
}

const AVATAR_TONES = ['green', 'gold', 'lilac', 'grey', 'red'] as const;

/** Initials on a soft tile, its colour fixed by the name. */
export function Avatar({ initials, seed, size, image }: { initials: string; seed?: string; size?: 'lg'; image?: string | null }) {
  const key = seed ?? initials;
  let hash = 0;
  for (const ch of key) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const tone = image ? 'grey' : AVATAR_TONES[hash % AVATAR_TONES.length];
  return (
    <span className={`avatar avatar--${tone}${size === 'lg' ? ' avatar--lg' : ''}`} aria-hidden>
      {image ? <img src={image} alt="" /> : initials}
    </span>
  );
}

export function ProBadge() {
  return (
    <span className="pro-badge">
      <Icon name="crown" size={11} stroke={2} />
      {t('commonPro')}
    </span>
  );
}

interface FieldShell {
  label: ReactNode;
  help?: ReactNode;
  error?: string | null;
  aside?: ReactNode;
  className?: string;
}

function useField(error?: string | null, help?: ReactNode) {
  const id = useId();
  const describedBy = [error && `${id}-error`, help && `${id}-help`].filter(Boolean).join(' ') || undefined;
  return { id, describedBy };
}

function FieldFrame({ id, label, help, error, aside, className, children }: FieldShell & { id: string; children: ReactNode }) {
  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <span className="field__label">
        <label htmlFor={id}>{label}</label>
        {aside}
      </span>
      {children}
      {error && (
        <span className="error" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
      {help && !error && (
        <span className="help" id={`${id}-help`}>
          {help}
        </span>
      )}
    </div>
  );
}

export function TextField({ label, help, error, aside, className, ...input }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const { id, describedBy } = useField(error, help);
  return (
    <FieldFrame id={id} label={label} help={help} error={error} aside={aside} className={className}>
      <input id={id} className="input" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...input} />
    </FieldFrame>
  );
}

export function TextArea({ label, help, error, aside, className, ...input }: FieldShell & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { id, describedBy } = useField(error, help);
  return (
    <FieldFrame id={id} label={label} help={help} error={error} aside={aside} className={className}>
      <textarea id={id} className="input" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...input} />
    </FieldFrame>
  );
}

export function SelectField({ label, help, error, aside, className, children, ...select }: FieldShell & SelectHTMLAttributes<HTMLSelectElement>) {
  const { id, describedBy } = useField(error, help);
  return (
    <FieldFrame id={id} label={label} help={help} error={error} aside={aside} className={className}>
      <select id={id} className="input" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...select}>
        {children}
      </select>
    </FieldFrame>
  );
}

/** A labelled on/off switch, a real checkbox underneath. */
export function Switch({ label, detail, checked, onChange, disabled }: { label: ReactNode; detail?: ReactNode; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  const id = useId();
  return (
    <label className="check-row" htmlFor={id}>
      <span className="grow stack-1">
        <span className="body-strong">{label}</span>
        {detail && <span className="caption">{detail}</span>}
      </span>
      <span className="switch">
        <input id={id} type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span aria-hidden />
      </span>
    </label>
  );
}

/** The page's title row: a serif headline, a line under it and actions. */
export function PageHead({ title, subtitle, back, actions }: { title: ReactNode; subtitle?: ReactNode; back?: { to: string; label: string }; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="page-head__title">
        {back && (
          <Link to={back.to} className="back-link">
            <Icon name="back" size={16} />
            {back.label}
          </Link>
        )}
        <h1 className="display">{title}</h1>
        {subtitle && <p className="body muted">{subtitle}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </header>
  );
}

/** Paper sheets on a soft halo, for empty lists. */
export function EmptyArt({ badge = 'plus' }: { badge?: IconName }) {
  return (
    <div className="art" aria-hidden>
      <div className="art__halo" />
      <div className="art__sheet art__sheet--back">
        <i className="art__accent" />
        <i />
        <i style={{ width: '70%' }} />
      </div>
      <div className="art__sheet art__sheet--front">
        <i className="art__accent" />
        <i />
        <i style={{ width: '60%' }} />
        <i style={{ width: '80%' }} />
        <i className="art__ink" />
      </div>
      <span className="art__badge">
        <Icon name={badge} size={20} stroke={2.2} />
      </span>
    </div>
  );
}

export function EmptyState({ title, body, action, badge }: { title: string; body?: string; action?: ReactNode; badge?: IconName }) {
  return (
    <div className="empty">
      <EmptyArt badge={badge} />
      <h2 className="title-serif">{title}</h2>
      {body && <p className="body muted">{body}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%', radius }: { height?: number; width?: number | string; radius?: number }) {
  return <div className="skel" style={{ height, width, borderRadius: radius }} aria-hidden />;
}

/** A loading page: the shape of what is coming, announced once. */
export function PageLoading() {
  return (
    <div className="page" aria-busy="true">
      <span className="vh" role="status">
        {t('commonLoading')}
      </span>
      <Skeleton height={40} width={280} radius={12} />
      <Skeleton height={120} radius={22} />
      <Skeleton height={320} radius={22} />
    </div>
  );
}
